import React, { useState, useEffect } from 'react';
import { apiUrl } from '../config';
import ServiceAccountSection from './ServiceAccountSection';
import CompanyForm from './CompanyForm';

interface Company {
  companyId: string;
  companyName: string;
  companyUrl: string;
  companyState: string;
  isPrimary?: boolean;
  primaryContact?: string;
  address?: string;
  primaryPhone?: string;
  primaryEmail?: string;
  enableInboundNotifications?: boolean;
  enableInboundDownloads?: boolean;
  allowedIpAddresses?: string; // Comma-separated IP addresses or CIDR blocks
  allowAllIps?: boolean; // Flag to allow 0.0.0.0/0
  sftpAuthType?: 'password' | 'ssh-key';
  sftpUsername?: string;
  sftpPassword?: string;
  sshPublicKey?: string;
  serviceAccount?: {
    username: string;
    email: string;
    role: string;
    createdAt: string;
  };
}

interface SharedCreateCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCompanyCreated: (company: Company) => void;
  includeServiceAccount?: boolean;
  mode?: 'create' | 'edit';
  initialData?: Partial<Company>;
  companyId?: string;
}

export default function SharedCreateCompanyModal({
  isOpen,
  onClose,
  onCompanyCreated,
  includeServiceAccount = false,
  mode = 'create',
  initialData = {},
  companyId,
}: SharedCreateCompanyModalProps) {
  const [form, setForm] = useState(() => {
    return {
      companyName: initialData?.companyName || '',
      companyUrl: initialData?.companyUrl || '',
      companyState: initialData?.companyState || '',
      primaryContact: initialData?.primaryContact || '',
      address: initialData?.address || '',
      primaryPhone: initialData?.primaryPhone || '',
      primaryEmail: initialData?.primaryEmail || initialData?.serviceAccount?.email || '',
      enableInboundNotifications: initialData?.enableInboundNotifications ?? false,
      enableInboundDownloads: initialData?.enableInboundDownloads ?? false,
      allowedIpAddresses: initialData?.allowedIpAddresses || '',
      allowAllIps: initialData?.allowAllIps || false,
      createServiceAccount: false,
      serviceAccountPasswordOption: 'generated' as 'generated' | 'custom' | 'ssh-key',
      serviceAccountPassword: '',
    };
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Initialize form data only once when modal opens or mode changes
  useEffect(() => {
    if (mode === 'edit' && initialData && companyId) {
      console.log('🔍 Edit mode - initializing form with company data');
      setForm({
        companyName: initialData?.companyName || '',
        companyUrl: initialData?.companyUrl || '',
        companyState: initialData?.companyState || '',
        primaryContact: initialData?.primaryContact || '',
        address: initialData?.address || '',
        primaryPhone: initialData?.primaryPhone || '',
        primaryEmail: initialData?.primaryEmail || initialData?.serviceAccount?.email || '',
        enableInboundNotifications: initialData?.enableInboundNotifications ?? false,
        enableInboundDownloads: initialData?.enableInboundDownloads ?? false,
        allowedIpAddresses: initialData?.allowedIpAddresses || '',
        allowAllIps: initialData?.allowAllIps || false,
        createServiceAccount: false,
        serviceAccountPasswordOption: 'generated' as 'generated' | 'custom' | 'ssh-key',
        serviceAccountPassword: '',
      });
    } else if (mode === 'create' && isOpen) {
      console.log('🔍 Create mode - initializing empty form');
      setForm({
        companyName: '',
        companyUrl: '',
        companyState: '',
        primaryContact: '',
        address: '',
        primaryPhone: '',
        primaryEmail: '',
        enableInboundNotifications: false,
        enableInboundDownloads: false,
        allowedIpAddresses: '',
        allowAllIps: false,
        createServiceAccount: false,
        serviceAccountPasswordOption: 'generated' as 'generated' | 'custom' | 'ssh-key',
        serviceAccountPassword: '',
      });
      setErrorMessage('');
    }
  }, [mode, isOpen]); // Only depend on mode and isOpen, not initialData or companyId

  // Debug form state changes - removed to prevent infinite loops

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('🔍 Form submitted with data:', form);
    console.log('🔍 Form createServiceAccount value:', form.createServiceAccount);
    console.log('🔍 Form primaryEmail value:', form.primaryEmail);
    setIsLoading(true);
    setErrorMessage('');

    // Validate required fields
    if (!form.companyName || !form.companyUrl || !form.companyState || !form.primaryEmail) {
      setErrorMessage('Missing required fields: companyName, companyUrl, state, primaryEmail');
      setIsLoading(false);
      return;
    }

    try {
      // Create the company first
      const companyData = {
        companyName: form.companyName.trim(),
        companyUrl: form.companyUrl.trim(),
        companyState: form.companyState.trim(),
        primaryContact: form.primaryContact.trim() || undefined,
        address: form.address.trim() || undefined,
        primaryPhone: form.primaryPhone.trim() || undefined,
        primaryEmail: form.primaryEmail.trim() || undefined,
        enableInboundNotifications: form.enableInboundNotifications,
        enableInboundDownloads: form.enableInboundDownloads,
        allowedIpAddresses: form.allowedIpAddresses?.trim() || undefined,
        allowAllIps: form.allowAllIps || undefined,
      };

      console.log('🔍 Sending company data to API:', companyData);
      console.log('🔍 API URL:', apiUrl('companies'));

      const res = await fetch(apiUrl('companies'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(companyData),
      });

      console.log('🔍 API Response status:', res.status);
      console.log('🔍 API Response headers:', Object.fromEntries(res.headers.entries()));

      if (!res.ok) {
        const errText = await res.text();
        console.error('🔍 API Error response:', errText);
        throw new Error(errText || 'Company creation failed');
      }

      const data = await res.json();
      console.log('✅ Company created:', data);

      // Create service account if requested and includeServiceAccount is true
      let serviceAccountResult = null;
      if (includeServiceAccount && form.createServiceAccount && form.primaryEmail) {
        try {
          // Generate service account username with SVC_ format
          const cleanCompanyName = form.companyName.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
          const randomDigits = Math.floor(Math.random() * 1000)
            .toString()
            .padStart(3, '0');
          const serviceAccountUsername = `SVC_${cleanCompanyName}${randomDigits}`;

          const serviceAccountData = {
            companyName: form.companyName.trim(),
            companyEmail: form.primaryEmail.trim(),
            companyId: data.company?.companyId || data.companyId,
            username: serviceAccountUsername,
            customPassword:
              form.serviceAccountPasswordOption === 'custom'
                ? form.serviceAccountPassword
                : undefined,
            sshPublicKey:
              form.serviceAccountPasswordOption === 'ssh-key'
                ? form.serviceAccountPassword
                : undefined,
            sendWelcomeEmail: false,
          };

          const serviceRes = await fetch(apiUrl('create-user'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(serviceAccountData),
          });

          if (serviceRes.ok) {
            serviceAccountResult = await serviceRes.json();
            console.log('✅ Service account created:', serviceAccountResult);
          } else {
            console.warn('⚠️ Service account creation failed:', await serviceRes.text());
          }
        } catch (serviceError) {
          console.warn('⚠️ Service account creation failed (non-critical):', serviceError);
        }
      }

      // Call the callback with the new company data
      const newCompany: Company = {
        companyId: data.company?.companyId || data.companyId,
        companyName: data.company?.companyName || data.companyName,
        companyUrl: data.company?.companyUrl || data.companyUrl,
        companyState: data.company?.companyState || data.companyState,
        isPrimary: data.company?.isPrimary || data.isPrimary || false,
        primaryContact: form.primaryContact.trim() || undefined,
        address: form.address.trim() || undefined,
        primaryPhone: form.primaryPhone.trim() || undefined,
        primaryEmail: form.primaryEmail.trim() || undefined,
      };

      onCompanyCreated(newCompany);
      onClose();
    } catch (err: any) {
      console.error('❌ Error creating company:', err);
      setErrorMessage(err.message || 'Unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompanySubmit = async (formData: any) => {
    console.log('🔍 Form submitted with data:', formData);
    setIsLoading(true);
    setErrorMessage('');

    try {
      // Prepare the company data from the formData parameter
      const companyData = {
        companyName: formData.companyName.trim(),
        companyUrl: formData.companyUrl.trim(),
        companyState: formData.companyState.trim(),
        primaryContact: formData.primaryContact.trim() || undefined,
        address: formData.address.trim() || undefined,
        primaryPhone: formData.primaryPhone.trim() || undefined,
        primaryEmail: formData.primaryEmail.trim(),
        enableInboundNotifications: formData.enableInboundNotifications,
        enableInboundDownloads: formData.enableInboundDownloads,
        allowedIpAddresses: formData.allowedIpAddresses?.trim() || undefined,
      allowAllIps: formData.allowAllIps || undefined,
        createServiceAccount: formData.createServiceAccount || false,
      };

      let company;

      if (mode === 'edit' && companyId) {
        // Update existing company
        console.log('🔍 Updating company with data:', companyData);
        const response = await fetch(apiUrl(`/companies/${companyId}`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(companyData),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to update company');
        }

        company = await response.json();
        console.log('✅ Company updated successfully:', company);
        console.log(
          '🔍 Company object details - companyId:',
          company.companyId,
          'company.companyId:',
          company.company?.companyId
        );
      } else {
        // Create new company
        console.log('🔍 Creating company with data:', companyData);
        const response = await fetch(apiUrl('/companies'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(companyData),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to create company');
        }

        company = await response.json();
        console.log('✅ Company created successfully:', company);
      }

      if (mode === 'create') {
        // Service account is automatically created by the backend during company creation
        console.log('✅ Company created with automatic service account');
      } else {
        console.log('✅ Company updated successfully');
      }

      onCompanyCreated(company);
      onClose();
    } catch (error: any) {
      console.error('❌ Error creating company:', error);
      setErrorMessage(error.message || 'Failed to create company');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setForm({
      companyName: '',
      companyUrl: '',
      companyState: '',
      primaryContact: '',
      address: '',
      primaryPhone: '',
      primaryEmail: '',
      enableInboundNotifications: false,
      enableInboundDownloads: false,
      allowedIpAddresses: '',
      allowAllIps: false,
      createServiceAccount: false,
      serviceAccountPasswordOption: 'generated',
      serviceAccountPassword: '',
    });

    setErrorMessage('');
    onClose();
  };

  // Reset form when modal opens (only for create mode)
  React.useEffect(() => {
    if (isOpen && mode === 'create') {
      setForm({
        companyName: '',
        companyUrl: '',
        companyState: '',
        primaryContact: '',
        address: '',
        primaryPhone: '',
        primaryEmail: '',
        enableInboundNotifications: false,
        enableInboundDownloads: false,
        allowedIpAddresses: '',
        allowAllIps: false,
        createServiceAccount: false,
        serviceAccountPasswordOption: 'generated',
        serviceAccountPassword: '',
      });
      setErrorMessage('');
    }
  }, [isOpen, mode]);

  // Reset form when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setForm({
        companyName: '',
        companyUrl: '',
        companyState: '',
        primaryContact: '',
        address: '',
        primaryPhone: '',
        primaryEmail: '',
        enableInboundNotifications: false,
        enableInboundDownloads: false,
        allowedIpAddresses: '',
        allowAllIps: false,
        createServiceAccount: false,
        serviceAccountPasswordOption: 'generated',
        serviceAccountPassword: '',
      });
      setErrorMessage('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  console.log('🔍 SharedCreateCompanyModal rendering with isOpen:', isOpen);
  console.log('🔍 Form state:', form);
  console.log('🔍 Error message:', errorMessage);
  console.log('🔍 Props - mode:', mode, 'initialData:', initialData, 'companyId:', companyId);
  console.log(
    '🔍 Form primaryEmail:',
    form.primaryEmail,
    'initialData primaryEmail:',
    initialData.primaryEmail
  );

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
      <div className='bg-slate-800 border border-white/10 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto'>
        <div className='flex justify-between items-center mb-4'>
          <h2 className='text-xl font-semibold text-slate-100'>
            {mode === 'edit' ? 'Edit Company' : 'Create New Company'}
          </h2>
          <button
            onClick={handleCancel}
            className='text-slate-400 hover:text-slate-200 text-2xl leading-none'
          >
            ×
          </button>
        </div>

        {/* Notification Settings - Direct implementation like SFTP connector */}
        <div className='space-y-4 pt-4 border-t border-white/10'>
          <h3 className='text-lg font-semibold text-slate-200 mb-4'>File Notification Settings</h3>

          {(() => {
            console.log(
              '🔍 Rendering notification checkboxes - enableInboundNotifications:',
              form.enableInboundNotifications,
              'enableInboundDownloads:',
              form.enableInboundDownloads
            );
            return null;
          })()}

          <div>
            <label className='flex items-center space-x-3'>
              <input
                type='checkbox'
                name='enableInboundNotifications'
                checked={form.enableInboundNotifications}
                onChange={e => {
                  console.log('🔍 enableInboundNotifications checkbox changed:', e.target.checked);
                  setForm(prev => ({ ...prev, enableInboundNotifications: e.target.checked }));
                }}
                className='w-4 h-4 text-indigo-600 bg-slate-700 border-slate-600 rounded focus:ring-indigo-500 focus:ring-2'
              />
              <div>
                <span className='text-sm font-medium text-slate-300'>
                  Enable Inbound File Notifications
                </span>
                <p className='text-xs text-slate-400 mt-1'>
                  When enabled, users will receive email notifications when files are uploaded to
                  inbound folders. When disabled, no notifications will be sent.
                </p>
              </div>
            </label>
          </div>

          <div>
            <label className='flex items-center space-x-3'>
              <input
                type='checkbox'
                name='enableInboundDownloads'
                checked={form.enableInboundDownloads}
                onChange={e => {
                  console.log('🔍 enableInboundDownloads checkbox changed:', e.target.checked);
                  setForm(prev => ({ ...prev, enableInboundDownloads: e.target.checked }));
                }}
                className='w-4 h-4 text-indigo-600 bg-slate-700 border-slate-600 rounded focus:ring-indigo-500 focus:ring-2'
                disabled={!form.enableInboundNotifications}
              />
              <div>
                <span
                  className={`text-sm font-medium ${form.enableInboundNotifications ? 'text-slate-300' : 'text-slate-500'}`}
                >
                  Enable Download Links in Inbound File Notifications
                </span>
                <p className='text-xs text-slate-400 mt-1'>
                  When enabled, users will receive direct download links for inbound files. When
                  disabled, users will only be notified that files are available. Requires
                  notifications to be enabled above.
                </p>
              </div>
            </label>
          </div>
        </div>

        {(() => {
          console.log('🔍 About to render CompanyForm with formData:', form);
          console.log(
            '🔍 About to render CompanyForm - enableInboundNotifications:',
            form.enableInboundNotifications
          );
          console.log(
            '🔍 About to render CompanyForm - enableInboundDownloads:',
            form.enableInboundDownloads
          );
          return null;
        })()}
        <CompanyForm
          formData={form}
          mode={mode}
          onChange={(field, value) => {
            console.log('🔍 CompanyForm onChange called:', field, value);
            setForm(prev => ({ ...prev, [field]: value }));
          }}
          onSubmit={async data => {
            console.log('🔍 CompanyForm submitted with data:', data);
            console.log('🔍 Current form state:', form);
            console.log('🔍 Validation check:', {
              companyName: !!data.companyName,
              companyUrl: !!data.companyUrl,
              companyState: !!data.companyState,
              primaryEmail: !!data.primaryEmail,
            });
            // Validate the data directly from CompanyForm
            if (!data.companyName || !data.companyUrl || !data.companyState || !data.primaryEmail) {
              console.log('❌ Validation failed - missing required fields');
              setErrorMessage('Company name, URL, state, and service account email are required');
              return;
            }
            console.log('✅ Validation passed - proceeding with submission');
            // Merge company data with current form state (including service account data)
            const mergedData = {
              ...form,
              ...data,
              // Include service account data from form state
              createServiceAccount: form.createServiceAccount,
              serviceAccountPasswordOption: form.serviceAccountPasswordOption,
              serviceAccountPassword: form.serviceAccountPassword,
            };
            console.log('🔍 Merged form data for submission:', mergedData);
            // Update form state and submit with merged data
            setForm(prev => ({ ...prev, ...data }));
            await handleCompanySubmit(mergedData);
          }}
          onCancel={handleCancel}
          loading={isLoading}
          errorMessage={errorMessage}
        />

        {/* Service Account Section - Only show if includeServiceAccount is true */}
        {includeServiceAccount && (
          <ServiceAccountSection
            existingServiceAccount={initialData.serviceAccount}
            onCreateServiceAccount={data => {
              setForm(prev => {
                // Preserve current notification settings from form state, not initialData
                return {
                  ...prev,
                  createServiceAccount: data.createServiceAccount,
                  serviceAccountPasswordOption: data.serviceAccountPasswordOption,
                  serviceAccountPassword: data.serviceAccountPassword,
                  // Keep existing notification settings - don't reset them
                  // enableInboundNotifications and enableInboundDownloads are preserved from prev
                };
              });
            }}
            onPasswordReset={async (username: string) => {
              try {
                const res = await fetch(`${apiUrl('reset-service-password')}`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    username,
                  }),
                });

                if (!res.ok) {
                  const errorText = await res.text();
                  throw new Error(errorText);
                }

                const result = await res.json();
                setErrorMessage(
                  `✅ Service account password reset successfully - New password: ${result.newPassword}`
                );
              } catch (err: any) {
                setErrorMessage(`❌ Failed to reset service account password: ${err.message}`);
              }
            }}
            onDelete={async (username: string) => {
              // TODO: Replace with proper confirmation dialog
              // if (!confirm(`Are you sure you want to delete the service account "${username}"?`))
              //   return;

              try {
                const res = await fetch(`${apiUrl('users')}/${encodeURIComponent(username)}`, {
                  method: 'DELETE',
                });

                if (!res.ok) throw new Error(await res.text());

                setErrorMessage('✅ Service account deleted successfully');
                // Reload the company data to refresh the service account info
                if (onCompanyCreated && companyId) {
                  // Trigger a reload by calling the callback with updated data
                  onCompanyCreated({
                    companyId,
                    companyName: initialData.companyName || '',
                    companyUrl: initialData.companyUrl || '',
                    companyState: initialData.companyState || '',
                    primaryContact: initialData.primaryContact,
                    address: initialData.address,
                    primaryPhone: initialData.primaryPhone,
                    primaryEmail: initialData.primaryEmail,
                    serviceAccount: undefined,
                  });
                }
              } catch (err: any) {
                setErrorMessage(`❌ Failed to delete service account: ${err.message}`);
              }
            }}
          />
        )}
      </div>
    </div>
  );
}
